import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { HrMigrationProposal, HrMigrationRefreshResult } from '../hr.models';
import { HrService } from '../hr.service';
import { HrModuleNavComponent } from '../shared/hr-module-nav.component';

@Component({
  selector: 'app-hr-migration',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    ButtonModule,
    DialogModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageFormSectionComponent,
    PageDataPanelComponent,
    HrModuleNavComponent
  ],
  providers: [MessageService],
  templateUrl: './hr-migration.component.html',
  styleUrls: ['./hr-migration.component.scss']
})
export class HrMigrationComponent implements OnInit {
  private readonly hrService = inject(HrService);
  private readonly messageService = inject(MessageService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(PermissionService);

  loading = false;
  proposals: HrMigrationProposal[] = [];
  refreshResult?: HrMigrationRefreshResult;
  statusFilter = 'PENDING';
  dismissDialogVisible = false;
  dismissReason = '';
  selectedProposal?: HrMigrationProposal;

  readonly statusOptions = ['PENDING', 'APPROVED', 'DISMISSED'].map(value => ({ label: this.format(value), value }));
  readonly canManageMigration = this.permissionService.hasPermission(app_permissions.hr.migration.manage);

  ngOnInit(): void {
    this.load();
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Pending', value: this.proposals.filter(item => item.proposalStatus === 'PENDING').length, tone: 'warn' },
      { label: 'Customers Scanned', value: this.refreshResult?.scannedCustomers ?? '-' },
      { label: 'Drivers Scanned', value: this.refreshResult?.scannedDrivers ?? '-' },
      { label: 'Created', value: this.refreshResult?.createdProposals ?? '-' },
      { label: 'Refreshed', value: this.refreshResult?.refreshedProposals ?? '-' }
    ];
  }

  load(): void {
    this.loading = true;
    this.hrService.migrationProposals(undefined, this.statusFilter).subscribe({
      next: response => this.proposals = response.data ?? [],
      error: () => {
        this.proposals = [];
        this.messageService.add({ severity: 'error', summary: 'HR', detail: 'Unable to load proposals.' });
      },
      complete: () => this.loading = false
    });
  }

  refresh(): void {
    if (!this.canManageMigration) return;
    this.loading = true;
    this.hrService.refreshMigrationProposals().subscribe({
      next: response => {
        this.refreshResult = response.data;
        this.messageService.add({ severity: 'success', summary: 'HR', detail: 'Migration proposals refreshed.' });
        this.load();
      },
      error: () => {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'HR', detail: 'Unable to refresh proposals.' });
      }
    });
  }

  approve(proposal: HrMigrationProposal): void {
    if (!this.canManageMigration) return;
    if (!proposal.proposalId) return;
    this.hrService.approveMigrationProposal(proposal.proposalId).subscribe({
      next: response => {
        this.messageService.add({ severity: 'success', summary: 'HR', detail: 'Proposal approved.' });
        if (response.data?.employeeId) {
          this.router.navigate(['/hr/employees', response.data.employeeId]);
        } else {
          this.load();
        }
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR', detail: 'Unable to approve proposal.' })
    });
  }

  openDismissDialog(proposal: HrMigrationProposal): void {
    if (!this.canManageMigration) return;
    if (!proposal.proposalId) return;
    this.selectedProposal = proposal;
    this.dismissReason = proposal.dismissedReason || '';
    this.dismissDialogVisible = true;
  }

  confirmDismiss(): void {
    if (!this.canManageMigration) return;
    const proposalId = this.selectedProposal?.proposalId;
    if (!proposalId) return;
    this.hrService.dismissMigrationProposal(proposalId, this.dismissReason || undefined).subscribe({
      next: () => {
        this.dismissDialogVisible = false;
        this.selectedProposal = undefined;
        this.dismissReason = '';
        this.messageService.add({ severity: 'success', summary: 'HR', detail: 'Proposal dismissed.' });
        this.load();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR', detail: 'Unable to dismiss proposal.' })
    });
  }

  format(value?: string): string {
    return (value || '-').toLowerCase().split('_').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
  }
}
